import { useMemo } from 'react';
import { calculateTestStatus } from '../utils/calibrationStatus';
import { TEST_STATUS, type OverallTestStatus } from '../types';
import type { BasicReadingTestPoint } from './useCalibrationRuntime/types';

export type DatasheetGroup = {
  groupKey: string;
  groupLabel: string;
  unit: string;
  functionSegment: string;
  points: BasicReadingTestPoint[];
  total: number;
  asFoundDone: number;
  asLeftDone: number;
  asFoundStatus: OverallTestStatus;
  asLeftStatus: OverallTestStatus;
  overallStatus: OverallTestStatus;
};

const FALLBACK_GROUP_KEY = 'points';
const FALLBACK_GROUP_LABEL = 'Test Points';

const pointPasses = (
  point: BasicReadingTestPoint,
  value: number | null,
  result: 'Pass' | 'Fail' | null
): boolean => {
  if (result !== null) return result === 'Pass';
  if (value === null) return false;

  const error = Number((value - point.nominalValue).toFixed(10));
  return Math.abs(error) <= point.tolerance;
};

const deriveSideStatus = (
  points: BasicReadingTestPoint[],
  side: 'asFound' | 'asLeft'
): { done: number; status: OverallTestStatus } => {
  const withValue = points.filter(p => p[side] !== null);
  const passed =
    withValue.length === points.length &&
    points.every(p =>
      pointPasses(
        p,
        p[side],
        side === 'asFound' ? p.asFoundResult : p.asLeftResult
      )
    );

  return {
    done: withValue.length,
    status: calculateTestStatus(points.length, withValue.length, passed)
  };
};

const deriveOverallStatus = (
  asFoundStatus: OverallTestStatus,
  asLeftStatus: OverallTestStatus
): OverallTestStatus => {
  if (
    asFoundStatus === TEST_STATUS.NOT_STARTED &&
    asLeftStatus === TEST_STATUS.NOT_STARTED
  ) {
    return TEST_STATUS.NOT_STARTED;
  }

  if (
    asFoundStatus === TEST_STATUS.NOT_STARTED ||
    asLeftStatus === TEST_STATUS.NOT_STARTED ||
    asFoundStatus === TEST_STATUS.IN_PROGRESS ||
    asLeftStatus === TEST_STATUS.IN_PROGRESS
  ) {
    return TEST_STATUS.IN_PROGRESS;
  }

  return asLeftStatus === TEST_STATUS.PASS
    ? TEST_STATUS.PASS
    : TEST_STATUS.FAIL;
};

const deriveFunctionSegment = (
  groupKey: string,
  points: BasicReadingTestPoint[]
): string => {
  const base = groupKey.toUpperCase();
  const frequencies = points.map(p => p.frequency ?? null);
  const firstFrequency = frequencies[0];

  const isUniformFrequency =
    firstFrequency !== null &&
    firstFrequency !== undefined &&
    frequencies.every(f => f === firstFrequency);

  return isUniformFrequency ? `${base} · ${firstFrequency} Hz` : base;
};

export const groupDatasheetPoints = (
  testPoints: BasicReadingTestPoint[]
): DatasheetGroup[] => {
  const groupOrder: string[] = [];
  const pointsByGroup = new Map<string, BasicReadingTestPoint[]>();

  for (const point of testPoints) {
    const key = point.groupKey ?? FALLBACK_GROUP_KEY;
    const existing = pointsByGroup.get(key);

    if (existing) {
      existing.push(point);
    } else {
      groupOrder.push(key);
      pointsByGroup.set(key, [point]);
    }
  }

  return groupOrder.map(groupKey => {
    const points = pointsByGroup.get(groupKey) ?? [];
    const firstPoint = points[0];
    const asFound = deriveSideStatus(points, 'asFound');
    const asLeft = deriveSideStatus(points, 'asLeft');

    return {
      groupKey,
      groupLabel:
        firstPoint?.groupLabel ??
        (groupKey === FALLBACK_GROUP_KEY
          ? FALLBACK_GROUP_LABEL
          : groupKey.toUpperCase()),
      unit: firstPoint?.unit ?? firstPoint?.asFoundUnit ?? '',
      functionSegment: deriveFunctionSegment(groupKey, points),
      points,
      total: points.length,
      asFoundDone: asFound.done,
      asLeftDone: asLeft.done,
      asFoundStatus: asFound.status,
      asLeftStatus: asLeft.status,
      overallStatus: deriveOverallStatus(asFound.status, asLeft.status)
    };
  });
};

export const useGroupedDatasheet = (
  testPoints: BasicReadingTestPoint[] | null | undefined
): DatasheetGroup[] =>
  useMemo(() => groupDatasheetPoints(testPoints ?? []), [testPoints]);
